/**
 * Small pure text/HTML formatting helpers shared by every place that
 * renders an ingredients list or preparation steps from node data —
 * RecipeTextRenderer.ts (the full RecipeStore-backed recipe page view) and
 * the ingredient explorer's read-only elaboration-subgraph view
 * (explorer/subgraphElaboration.ts), which has no RecipeStore to hang off
 * of. Kept in one place so quantity formatting, Catalan preposition
 * elision, and ingredient-thumbnail markup can't drift between the two.
 */
import { getDefaultImageUrl } from './imageUtils.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { TIME_UNITS, TimeUnitDef } from '../time_units.js';

/** Vowels (including Catalan diacritics) for elision of «de» → «d'» */
const VOWELS = new Set('aeiouàèéìíòóùúïüAEIOUÀÈÉÌÍÒÓÙÚÏÜ');

export function capitalizeFirst(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Capitalize the first visible (non-HTML-tag) character in a string.
 */
export function capitalizeFirstVisible(html: string): string {
  if (!html) return html;
  let inTag = false;
  for (let i = 0; i < html.length; i++) {
    const ch = html[i];
    if (ch === '<') { inTag = true; continue; }
    if (ch === '>') { inTag = false; continue; }
    if (!inTag && /[a-zà-ü]/i.test(ch)) {
      return html.slice(0, i) + ch.toLocaleUpperCase() + html.slice(i + 1);
    }
  }
  return html;
}

/**
 * Lowercase the first visible (non-HTML-tag) character in a string — the
 * mirror of capitalizeFirstVisible(), for text that's being spliced in
 * after something else (e.g. prefixed with "Amb {tool}: ") rather than
 * starting a sentence of its own.
 */
export function decapitalizeFirstVisible(html: string): string {
  if (!html) return html;
  let inTag = false;
  for (let i = 0; i < html.length; i++) {
    const ch = html[i];
    if (ch === '<') { inTag = true; continue; }
    if (ch === '>') { inTag = false; continue; }
    if (!inTag && /[a-zà-ü]/i.test(ch)) {
      return html.slice(0, i) + ch.toLocaleLowerCase() + html.slice(i + 1);
    }
  }
  return html;
}

/**
 * Catalan preposition with elision: «de» → «d'» before a vowel.
 */
export function preposition(name: string): string {
  return VOWELS.has(name.charAt(0)) ? "d'" : 'de ';
}

/**
 * Small circular thumbnail for an ingredient row, using its default pool
 * image — same size/treatment as the ingredient thumbnails elsewhere in
 * the app (SearchSection's chips, the explorer's rows). Empty string
 * (not a placeholder box) when there's no image, so ingredients without
 * one just show their name directly, matching the layout before images
 * were added here.
 */
export function ingredientThumbnailHtml(images: Parameters<typeof getDefaultImageUrl>[0]): string {
  const url = getDefaultImageUrl(images);
  if (!url) return '';
  return `    <img src="${escapeHtml(url)}" alt="" class="rounded-full flex-shrink-0" style="width:20px;height:20px;object-fit:cover;" />`;
}

/**
 * Merge quantities by summing amounts with the same unit.
 */
export function mergeQuantities(quantities: { amount: number; unit: string }[]): { amount: number; unit: string }[] {
  if (quantities.length === 0) return [];
  const byUnit = new Map<string, number>();
  for (const q of quantities) {
    byUnit.set(q.unit, (byUnit.get(q.unit) ?? 0) + q.amount);
  }
  return Array.from(byUnit.entries()).map(([unit, amount]) => ({ amount, unit }));
}

/**
 * Format a number using Catalan conventions (comma as decimal separator).
 */
export function formatNumber(n: number): string {
  const str = n.toString();
  if (str.includes('.')) {
    return str.replace('.', ',');
  }
  return str;
}

const TIME_UNITS_DESC = [...TIME_UNITS].sort((a, b) => b.seconds - a.seconds);

export interface DurationComposite {
  majorAmount: number;
  majorUnit: TimeUnitDef;
  /** 0 when minorUnit is null, or when the remainder in that unit is 0. */
  minorAmount: number;
  /** The TIME_UNITS entry one step below majorUnit, or null when majorUnit
   * is already the smallest (segons) — nothing to break the remainder
   * into. Always present (even when minorAmount is 0) so a caller like
   * the composite duration input always knows which unit to label its
   * second field with. */
  minorUnit: TimeUnitDef | null;
}

/**
 * Break a duration (total seconds) into a given major unit and the
 * whole-number remainder in a given minor unit — e.g. splitting 150 at
 * {major: hores, minor: minuts} gives {0, 2} (0 hores, 2 minuts; the 30
 * leftover seconds aren't representable in minuts, so they're dropped
 * from the result — see DurationInput.ts's doc comment on why that's
 * fine: the *caller* keeps the original total around separately, this
 * only computes what to display for one specific unit pair).
 */
export function splitDurationAtUnit(
  totalSeconds: number,
  majorUnit: TimeUnitDef,
  minorUnit: TimeUnitDef | null,
): { majorAmount: number; minorAmount: number } {
  const majorAmount = Math.floor(totalSeconds / majorUnit.seconds);
  const remainder = totalSeconds - majorAmount * majorUnit.seconds;
  const minorAmount = minorUnit ? Math.floor(remainder / minorUnit.seconds) : 0;
  return { majorAmount, minorAmount };
}

/**
 * Break a duration (total seconds) into a major unit (the largest one
 * that fits at least once) and the whole-number remainder in the next
 * smaller unit — e.g. 5400 -> {1, hora, 30, minuts}, 266400 -> {3, dies,
 * 2, hores}, 1200 -> {20, minuts, 0, segons}. The single source of truth
 * for both the display formatters below and the composite duration
 * input's *initial* unit pair (DurationInput.ts switches unit pairs
 * itself afterward via splitDurationAtUnit() above, anchored to whichever
 * pair the user has scaled to) — so they can never drift apart on which
 * two units a given value breaks into by default.
 */
export function secondsToComposite(totalSeconds: number): DurationComposite {
  if (!totalSeconds || totalSeconds <= 0) {
    const smallest = TIME_UNITS_DESC[TIME_UNITS_DESC.length - 1];
    return { majorAmount: 0, majorUnit: smallest, minorAmount: 0, minorUnit: null };
  }
  const majorIdx = TIME_UNITS_DESC.findIndex(u => totalSeconds >= u.seconds);
  const major = majorIdx === -1 ? TIME_UNITS_DESC[TIME_UNITS_DESC.length - 1] : TIME_UNITS_DESC[majorIdx];
  const minorUnit = majorIdx === -1 ? null : (TIME_UNITS_DESC[majorIdx + 1] ?? null);
  const { majorAmount, minorAmount } = splitDurationAtUnit(totalSeconds, major, minorUnit);
  return { majorAmount, majorUnit: major, minorAmount, minorUnit };
}

function durationParts(totalSeconds: number): { amount: number; unit: TimeUnitDef }[] {
  const { majorAmount, majorUnit, minorAmount, minorUnit } = secondsToComposite(totalSeconds);
  const parts = [{ amount: majorAmount, unit: majorUnit }];
  if (minorUnit && minorAmount > 0) {
    parts.push({ amount: minorAmount, unit: minorUnit });
  }
  return parts;
}

/**
 * Full-word display form for a duration (total seconds) — e.g. "1 hora i
 * 30 minuts". Used in prose (the `format_duration()` template builtin,
 * for a custom render_template that wants to spell it out); the clock
 * badge shown next to preparation steps uses formatDurationAbbreviated()
 * instead.
 */
export function formatDurationComposite(totalSeconds: number): string {
  return durationParts(totalSeconds)
    .map(({ amount, unit }) => `${amount} ${amount === 1 ? unit.singular : unit.plural}`)
    .join(' i ');
}

/**
 * Abbreviated display form for a duration (total seconds) — e.g. "1 h. 30
 * min.". Used by durationBadgeHtml() for the clock-badge shown next to
 * preparation steps and the node total-duration line.
 */
export function formatDurationAbbreviated(totalSeconds: number): string {
  return durationParts(totalSeconds)
    .map(({ amount, unit }) => `${amount} ${unit.abbreviation}`)
    .join(' ');
}

/**
 * The clock-badge markup shown next to a preparation step (or as a node's
 * total elaboration time) — grey by default, same color as the "//"
 * comment lines; pass `colorClass` to use a different text color
 * instead (e.g. the recipe-wide total next to the "Preparació" header,
 * which uses a dimmed green instead of grey). Abbreviated units,
 * flex-shrink-0 so it never wraps when the row's text is long. Returns
 * '' when there's nothing to show, so callers can just concatenate it
 * without an extra null check.
 */
export function durationBadgeHtml(seconds: number | null | undefined, colorClass = 'text-[#888]'): string {
  if (!seconds) return '';
  // ◷ (a plain Geometric Shapes glyph), not ⏱ — most fonts render ⏱ as a
  // colored emoji by default, which doesn't fit this grey/monochrome
  // badge; ◷ stays text regardless of font/platform. Same glyph the
  // Cronologia tab icon uses (see TabSelector.ts/GraphListToggle.ts).
  return `<span class="text-xs ${colorClass} font-mono flex-shrink-0">◷ ${escapeHtml(formatDurationAbbreviated(seconds))}</span>`;
}
